import PropTypes from "prop-types";
import { sanitize } from "helpers/sanitize";
import typeaheadHelpers from "helpers/typeahead-helpers";
import { useState, useRef, useEffect } from "react";
import ActionLogger from "action-logger";
import { isStorefrontNext } from "helpers/environment-helpers";
import { useSelector } from "react-redux";
import SearchResults from "./search-results";

const propTypes = {
  showSearchField: PropTypes.bool,
  isHamburgerOpen: PropTypes.bool,
  analyticsObjectType: PropTypes.string,
  inputId: PropTypes.string,
  setShowSearchField: PropTypes.func,
};

const SearchField = props => {
  const { showSearchField, isHamburgerOpen, analyticsObjectType, inputId, setShowSearchField } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [typeaheadPrompts, setTypeaheadPrompts] = useState([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const inputRef = useRef(null);
  const discoHost = useSelector(state => state.publicEnv?.discoHost);

  useEffect(() => {
    if (showSearchField || isHamburgerOpen) {
      inputRef.current?.focus();
    }
  }, [showSearchField, isHamburgerOpen]);

  const handleChange = async event => {
    const query = sanitize(event.target.value);

    setSearchQuery(query);

    setTypeaheadPrompts([]);

    // Typeahead only returns results for terms with more than 3 characters
    const prompts = await typeaheadHelpers.executeTypeaheadSearch(query, true, isStorefrontNext(), discoHost || "");

    setTypeaheadPrompts(prompts);
  };

  const handleSubmit = () => {
    setIsExecuting(true);
    ActionLogger.inferAction({
      objectType: analyticsObjectType,
      action: "execute_search",
      search_text: searchQuery,
    });
  };

  const handleBlur = () => {
    inputRef.current?.blur();
    setShowSearchField(false);
    setSearchQuery("");
    setTypeaheadPrompts([]);
  };

  return (
    <div
      data-test-id="searchbar-field-wrapper"
      className={`${isHamburgerOpen ? "relative" : "absolute"} !-ml-5 md:!-ml-4 w-full md:w-[30%] !h-9 md:!h-11`}>
      <form id="search-bar-form" onSubmit={handleSubmit} action="/products" method="get">
        <input
          data-test-id="searchbar-field"
          id={inputId}
          ref={inputRef}
          className={`!pl-12 ${
            typeaheadPrompts?.length ? "!rounded-t-md" : "!rounded-md"
          } md:!rounded-none !text-sm md:!text-md placeholder-inherit w-full !h-9 md:!h-11 md:transition-all md:duration-700 md:ease-in-out ${
            showSearchField || isHamburgerOpen ? "md:opacity-100" : "md: opacity-0 md:w-0 md:!p-0"
          }`}
          name={"filters[searchText]"}
          autoComplete="off"
          placeholder="Search for anything"
          onChange={handleChange}
          onBlur={handleBlur}
          value={searchQuery}
          readOnly={isExecuting}
        />
        {typeaheadPrompts?.length > 0 && (
          <SearchResults
            typeaheadPrompts={typeaheadPrompts}
            isHamburgerOpen={isHamburgerOpen}
            setSearchQuery={setSearchQuery}
            setTypeaheadPrompts={setTypeaheadPrompts}
            showSearchField={showSearchField}
          />
        )}
      </form>
    </div>
  );
};

SearchField.propTypes = propTypes;

export default SearchField;
